import { Fragment, useState, useEffect, useMemo } from 'react';
import tw from 'twin.macro';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import { makeStyles } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useMediaQuery } from 'react-responsive';
import clsx from 'clsx';
import { setFlightTripSearchInputs } from '../../../../../store/slices/tripSearch';
import flightSearchInput from '../../../../../helpers/selectors/flightSearchInput';
import { updateFlightSearchObj } from '../../../../../store/slices/flight';
import Filter from '../../../../Filters/Filter';
import Dropdown from '../../../../Dropdown';
import Checkbox from '../../../../Checkbox';
import TwRadio from '../../../../TwRadio';
import MarkdownToHtml from '../../../../MarkdownToHtml';

const useFormStyle = makeStyles(() => ({
  root: {
    '&': tw`flex-nowrap pl-4 w-full`,
  },
}));

const useRadioStyle = makeStyles(() => ({
  root: {
    '& .MuiFormControlLabel-label': {
      fontFamily: 'var(--fontFamilyBody)',
    },
  },
}));

const hasPassengerType = (r) => r.passengerType !== null && r.passengerType !== undefined;

const RateCodePreferencesFilter = ({ tripId, variant, filterBgColor }) => {
  const isTripSearch = !tripId;
  const { travelAgency } = useSelector((state) => state.user);
  const { searchInputs } = useSelector(flightSearchInput(tripId, isTripSearch));
  const flightRateCodes = useSelector((store) => store.user.flightRateCodes);
  const [options, setOptions] = useState([]);
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const formClasses = useFormStyle();
  const radioClasses = useRadioStyle();
  const isSmallScreen = useMediaQuery({ query: '(max-width: 768px)' });
  const doHidePreferredFlightRateCodes =
    travelAgency &&
    travelAgency.filterSettings &&
    travelAgency.filterSettings.doHidePreferredFlightRateCodes;
  const headerContent = travelAgency?.filterSettings?.flightRateCodesHeader;
  const footerContent = travelAgency?.filterSettings?.flightRateCodesFooter;

  const values = (searchInputs && searchInputs.rateCodes) || [];

  // Rate codes WITHOUT a passenger type: multi-select checkboxes (existing behavior).
  useEffect(() => {
    if (flightRateCodes && flightRateCodes.length) {
      setOptions(
        _.map(_.reject(flightRateCodes, hasPassengerType), (r) => ({
          value: r.selectionKey || r.rateCode,
          label: r.text && r.rateCode ? `${r.text} (${r.rateCode})` : r.rateCode || r.text,
        })),
      );
    }
  }, [flightRateCodes]);

  // Rate codes WITH a passenger type: grouped by type, shown as radio options.
  // Selecting a passenger type applies the `selectionKey` of every rate code belonging to that type.
  const passengerTypeOptions = useMemo(() => {
    const grouped = _.groupBy(_.filter(flightRateCodes || [], hasPassengerType), 'passengerType');
    return _.map(grouped, (codes, type) => {
      const typeCode = (codes[0] && codes[0].passengerTypeCode) || String(type);
      const labelText = _.uniq(_.map(codes, (c) => c.text || c.selectionKey)).join(' · ');
      return {
        typeCode,
        labelText,
        values: _.map(codes, 'selectionKey'),
      };
    });
  }, [flightRateCodes]);

  const passengerTypeValues = useMemo(
    () => _.flatMap(passengerTypeOptions, 'values'),
    [passengerTypeOptions],
  );

  // A passenger type is considered selected when all of its values are selected.
  const selectedPassengerType = useMemo(
    () =>
      _.find(
        passengerTypeOptions,
        (o) => o.values.length && _.every(o.values, (v) => values.includes(v)),
      ) || null,
    // oxlint-disable-next-line react/exhaustive-deps
    [passengerTypeOptions, searchInputs && searchInputs.rateCodes],
  );

  const isEmpty = useMemo(() => {
    if (selectedPassengerType) return false;
    if (!values || !values.length || !options || !options.length) return true;
    // to check empty value, consider values which are in options.
    // For some agencies, we have default values which are not in options
    const filteredValues = _.filter(values, (v) => !!_.find(options, (o) => o.value === v));
    return !filteredValues.length;
    // oxlint-disable-next-line react/exhaustive-deps
  }, [options, searchInputs && searchInputs.rateCodes, selectedPassengerType]);

  const label = useMemo(() => {
    if (isEmpty) return 'Rate Codes';
    const labels = [];
    if (selectedPassengerType) {
      labels.push(`${selectedPassengerType.labelText} (${selectedPassengerType.typeCode})`);
    }
    _.forEach(options, (o) => {
      if (values.includes(o.value)) labels.push(o.label);
    });
    return labels.length ? labels.join(', ') : 'Rate Codes';
    // oxlint-disable-next-line react/exhaustive-deps
  }, [isEmpty, options, searchInputs && searchInputs.rateCodes, selectedPassengerType]);

  const handleChange = (newValue) => {
    const payload = {
      tripId,
      searchObj: {
        rateCodes: newValue,
        exactMatchRateCodesOnly:
          newValue.length === 0 ? false : searchInputs.exactMatchRateCodesOnly,
        page: 1,
      },
    };

    if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
    else dispatch(updateFlightSearchObj(payload));
    track('rateCodePreferencesFilter.handleChange', {
      filterPreferences: JSON.stringify(newValue),
    });
  };

  const handleCheckboxChange = (itemValue, newValue) => {
    let newValues = [...values];
    if (newValue && !newValues.includes(itemValue)) {
      newValues.push(itemValue);
    } else if (!newValue) {
      newValues = newValues.filter((v) => v !== itemValue);
    }
    handleChange(newValues);
  };

  const handlePassengerTypeChange = (newTypeCode) => {
    // only one passenger type can be selected at a time:
    // remove values of every passenger type group, then add the newly selected group's values
    let newValues = _.filter(values, (v) => !passengerTypeValues.includes(v));
    if (newTypeCode) {
      const group = _.find(passengerTypeOptions, (o) => o.typeCode === newTypeCode);
      if (group) newValues = [...newValues, ...group.values];
    }
    handleChange(newValues);
  };

  const handleClear = () => {
    // keep only the values that are not user-selectable (e.g. agency defaults not in options)
    const newValues = _.filter(
      values,
      (v) => !_.find(options, (o) => o.value === v) && !passengerTypeValues.includes(v),
    );
    handleChange(newValues);
  };

  return flightRateCodes && flightRateCodes.length && !doHidePreferredFlightRateCodes ? (
    <Filter
      label={label}
      tooltipText={label}
      menuWidthClass="w-max"
      variant={isTripSearch ? undefined : variant}
      isFilterEmpty={isEmpty}
      handleClear={handleClear}
      widthInside="max-w-85"
      bgClassName={!isTripSearch ? '' : filterBgColor}
      dropdownAligning={isSmallScreen ? 'right' : 'left'}
    >
      {headerContent && (
        <Dropdown.Item withGridLayout={false}>
          <MarkdownToHtml content={headerContent} removeDefaultStyle />
        </Dropdown.Item>
      )}
      <Dropdown.Item withGridLayout={false}>
        {passengerTypeOptions.length ? (
          <RadioGroup
            className={clsx(
              'pl-4 w-full',
              options && options.length ? 'pb-2 mb-2 border-b border-gray-200' : '',
            )}
            classes={radioClasses}
            value={selectedPassengerType ? selectedPassengerType.typeCode : ''}
            onChange={(event, newValue) => handlePassengerTypeChange(newValue)}
          >
            {passengerTypeOptions.map((item) => (
              <FormControlLabel
                key={item.typeCode}
                value={item.typeCode}
                className="pb-1"
                control={
                  <TwRadio
                    onClick={() => {
                      // allow deselecting the currently selected passenger type
                      if (selectedPassengerType && selectedPassengerType.typeCode === item.typeCode)
                        handlePassengerTypeChange(null);
                    }}
                  />
                }
                label={
                  <span className="flex flex-col">
                    <span>{item.labelText}</span>
                    <span className="text-xs text-gray-500">{item.typeCode}</span>
                  </span>
                }
              />
            ))}
          </RadioGroup>
        ) : null}
        {options.length ? (
          <FormControl
            className="grid col-span-5 grid-cols-2 gap-y-2 w-full items-center"
            component="fieldset"
          >
            <FormGroup classes={formClasses} className="overflow-x-auto max-h-1">
              {options.map((item) => (
                <Fragment key={item.value}>
                  <Checkbox
                    checked={values.includes(item.value)}
                    name={item.label}
                    onChange={(event, newValue) => handleCheckboxChange(item.value, newValue)}
                    value={item.value}
                    label={item.label}
                  />
                </Fragment>
              ))}
            </FormGroup>
          </FormControl>
        ) : null}
      </Dropdown.Item>
      {footerContent && (
        <Dropdown.Item withGridLayout={false}>
          <MarkdownToHtml content={footerContent} className="border-t-2 pt-2" />
        </Dropdown.Item>
      )}
    </Filter>
  ) : (
    <></>
  );
};

RateCodePreferencesFilter.propTypes = {
  tripId: PropTypes.string,
  variant: PropTypes.string,
  filterBgColor: PropTypes.string,
};

RateCodePreferencesFilter.defaultProps = {
  tripId: undefined,
  filterBgColor: undefined,
  variant: 'outline',
};

export default RateCodePreferencesFilter;
